import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { STATUS_STYLES, money } from "../lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
  LineChart, Line,
} from "recharts";
import { Flag, TrendingUp, DollarSign, ChevronRight } from "lucide-react";

const TREND_COLORS = ["#BA0C2F", "#BA0C2F", "#2E8B57", "#C97A1B", "#7A3FBE", "#0E8A94", "#98A2AF"];
const TREND_LABELS = { 0: "Not Started", 1: "Critical", 2: "At Risk", 3: "On Track" };

export default function Dashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/status-trends").then((r) => setTrends(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="text-[#5A687C]">Loading dashboard…</div>;

  const total = Object.values(d.rollup).reduce((a, b) => a + b, 0);
  const wsCode = (id) => d.workstreams.find((w) => w.id === id)?.code || "";
  const trendData = trends && trends.series.length
    ? (trends.series[0].points || []).map((_, idx) => {
        const row = { name: `M${idx + 1}` };
        trends.series.forEach((s) => { row[s.code] = s.points[idx]?.value; });
        return row;
      })
    : [];
  const budgetData = d.budget.by_workstream.map((b) => ({ name: b.code, Planned: b.planned, Actual: b.actual }));
  const heatMax = Math.max(1, ...d.heatmap.flat());

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">Executive Dashboard</h1>
        <p className="text-[#5A687C] mt-1">Real-time program health — computed live from workstream and task data.</p>
      </div>

      {/* RAG rollup */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="rag-rollup">
        {["On Track", "At Risk", "Critical", "Not Started"].map((status) => {
          const count = d.rollup[status] || 0;
          const drivers = d.workstreams.filter((w) => (w.derived_status || w.status) === status);
          return (
            <div key={status} data-testid={`rollup-${status.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white rounded-[18px] border border-[#DCE2EA] p-5 shadow-[0_2px_10px_rgba(31,39,51,0.04)]">
              <div className="flex items-center justify-between">
                <span className="text-4xl font-extrabold" style={{ color: STATUS_STYLES[status].dot }}>{count}</span>
                <StatusBadge status={status} />
              </div>
              <div className="mt-2 text-xs text-[#5A687C]">{total ? Math.round((count / total) * 100) : 0}% of program</div>
              {(status === "At Risk" || status === "Critical") && drivers.length > 0 && (
                <div className="mt-3 space-y-1 border-t border-[#DCE2EA] pt-2">
                  {drivers.map((w) => (
                    <button key={w.id} onClick={() => nav(`/workstream/${w.id}`)}
                      className="w-full text-left text-xs font-semibold text-[#BA0C2F] hover:underline flex items-center justify-between">
                      {w.code} <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Budget */}
        <div className="lg:col-span-2 bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1F2733] flex items-center gap-2"><DollarSign className="h-5 w-5 text-[#BA0C2F]" /> Budget — Planned vs Actual</h2>
            <div className="text-sm text-[#5A687C]">{money(d.budget.actual)} of {money(d.budget.planned)}</div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={budgetData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE2EA" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#5A687C" }} />
              <YAxis tickFormatter={money} tick={{ fontSize: 11, fill: "#5A687C" }} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              <Bar dataKey="Planned" fill="#F1B9C3" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Actual" fill="#BA0C2F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Risk heatmap */}
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] mb-1">Risk Heatmap</h2>
          <p className="text-xs text-[#5A687C] mb-3">Likelihood × Impact (from RAID log)</p>
          <div className="flex gap-2">
            <div className="flex flex-col justify-between text-[10px] text-[#98A2AF] font-semibold py-1">
              {[5, 4, 3, 2, 1].map((n) => <span key={n}>{n}</span>)}
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-5 gap-1">
                {[4, 3, 2, 1, 0].map((li) =>
                  [0, 1, 2, 3, 4].map((im) => {
                    const c = d.heatmap[li][im];
                    const sev = (li + 1) * (im + 1);
                    const bg = sev >= 15 ? "#BA0C2F" : sev >= 8 ? "#C97A1B" : sev >= 4 ? "#2E8B57" : "#98A2AF";
                    return (
                      <button key={`${li}-${im}`} data-testid={`heatmap-cell-${li + 1}-${im + 1}`}
                        onClick={() => c && setSel({ li: li + 1, im: im + 1 })}
                        title={`Likelihood ${li + 1} × Impact ${im + 1}: ${c} risk(s)`}
                        className={`aspect-square rounded-md flex items-center justify-center text-xs font-bold transition-transform ${c ? "cursor-pointer hover:scale-105" : "cursor-default"} ${sel && sel.li === li + 1 && sel.im === im + 1 ? "ring-2 ring-[#1F2733]" : ""}`}
                        style={{ background: c ? bg : "#F1F3F6", color: c ? "#fff" : "#C4CEDB", opacity: c ? Math.max(0.5, c / heatMax) : 1 }}>
                        {c || ""}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="grid grid-cols-5 text-[10px] text-[#98A2AF] font-semibold mt-1 text-center">
                {[1, 2, 3, 4, 5].map((n) => <span key={n}>{n}</span>)}
              </div>
              <div className="text-[10px] text-[#98A2AF] text-center mt-1">Impact →</div>
            </div>
          </div>
          {sel && (
            <div className="mt-4 border-t border-[#DCE2EA] pt-3" data-testid="heatmap-drilldown">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#1F2733]">Likelihood {sel.li} × Impact {sel.im} · severity {sel.li * sel.im}</span>
                <button onClick={() => setSel(null)} className="text-xs font-semibold text-[#BA0C2F] hover:underline">Clear</button>
              </div>
              {d.risks.filter((r) => r.likelihood === sel.li && r.impact === sel.im).map((r) => (
                <button key={r.id} onClick={() => nav(`/workstream/${r.workstream_id}`)} className="mt-2 block text-left text-xs text-[#5A687C] hover:text-[#1F2733]">
                  <span className="font-bold text-[#BA0C2F]">{wsCode(r.workstream_id)}</span> — {r.description}
                </button>
              ))}
              {d.risks.filter((r) => r.likelihood === sel.li && r.impact === sel.im).length === 0 && (
                <div className="mt-2 text-xs text-[#98A2AF]">No risks recorded in this cell.</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Milestones */}
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><Flag className="h-5 w-5 text-[#BA0C2F]" /> Key Milestones</h2>
          <div className="space-y-2">
            {d.milestones.length === 0 && <div className="text-sm text-[#5A687C]">No upcoming milestones.</div>}
            {d.milestones.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F6FA]">
                <div className="text-xs font-bold text-[#BA0C2F] bg-[#FBEAED] rounded-lg px-2.5 py-1.5 w-24 text-center shrink-0">
                  {new Date(m.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-[#1F2733] truncate">{m.title}</div></div>
                <StatusBadge status={m.status} />
              </div>
            ))}
          </div>
        </div>

        {/* Workstream table */}
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-[#BA0C2F]" /> Workstream Health</h2>
          <div className="space-y-2">
            {d.workstreams.map((w) => (
              <button key={w.id} onClick={() => nav(`/workstream/${w.id}`)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F6FA] text-left">
                <span className="text-xs font-bold text-[#5A687C] w-14 shrink-0">{w.code}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1F2733] truncate">{w.title}</div>
                  <div className="h-1.5 bg-[#F1F3F6] rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${w.health.percent_complete}%`, background: STATUS_STYLES[w.derived_status || w.status].dot }} />
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {w.manual_override && <span title="Status manually set by PMO" className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#FFF8EC] text-[#824D0E] border border-[#FCE3B5]">manual</span>}
                  <StatusBadge status={w.derived_status || w.status} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {trendData.length > 0 && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="status-trends">
          <h2 className="font-bold text-[#1F2733] mb-1">Status Trends</h2>
          <p className="text-xs text-[#5A687C] mb-4">Workstream status over the last 5 months — see whether a risk is new or lingering.</p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE2EA" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#5A687C" }} />
              <YAxis domain={[0, 3]} ticks={[0, 1, 2, 3]} width={90} tickFormatter={(v) => TREND_LABELS[v]} tick={{ fontSize: 11, fill: "#5A687C" }} />
              <Tooltip formatter={(v) => TREND_LABELS[v]} />
              <Legend />
              {trends.series.map((s, i) => (
                <Line key={s.code} type="stepAfter" dataKey={s.code} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
